import { memo, useEffect, useState } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import { Typography } from '@myn/mui';

import { useAddMediaPlanRowMutation } from '../../../api/mediaPlansApiSlice.js';
import { BUILDER_ROW_TYPES } from '../../../constants/campaignBuilder.js';
import { dragItemTypes } from '../../../constants/myCampaigns.js';
import { useTrackingInitiator } from '../../../hooks/usePerformanceTracking.js';
import { useSelectedBuyingPlanId } from '../../../hooks/useSelectedBuyingPlanId.js';
import { useTableScale } from '../../../hooks/useTableScale.js';
import { DRAWER_TABS_WIDTH } from '../../../molecules/DrawerTabs/DrawerTabs.jsx';
import { TRACKING_EVENTS } from '../../../types/performanceTracking.js';
import { useAddMediaPlanRow } from '../../../utils/campaigns/myCampaigns.js';
import { ADD_PANEL_WIDTH } from '../AddRowsPanel/AddRowsPanel.jsx';

type Props = {
  rows: any;
  toggleRowExpanded: (id: number, isOpen: boolean) => void;
  setRowState: (id: number[], { isNewAdded }: { isNewAdded: boolean }) => void;
};

type RowToAddType = {
  channelLabel: string;
  channelName: string;
  label: string;
  rowType: string;
  options: {
    inverted: boolean;
    onDoubleClick: () => void;
  };
  platform: string;
};
const MAX_NUMBER_TARGETS = 3;
const CONTENT_PADDING_LEFT = 66;

const DropContainer = memo(
  ({ rows, toggleRowExpanded, setRowState }: Props) => {
    const { t } = useTranslation('@myn/planning');

    const scale = useTableScale();
    const selectedPlanId = useSelectedBuyingPlanId();
    const [addMediaPlanRow] = useAddMediaPlanRowMutation();
    const { onSuccess, onFailed } = useAddMediaPlanRow();

    const [rowToAdd, setRowToAdd] = useState<RowToAddType | null>(null);

    const startMeasurePerformance = useTrackingInitiator();

    // this api requests need refactoring
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: [dragItemTypes.ADD_ROW],
      drop: async (item: any) => {
        switch (item.rowType) {
          case BUILDER_ROW_TYPES.CHANNEL: {
            startMeasurePerformance(TRACKING_EVENTS.CHANNEL_ADDITION);
            const res = await addMediaPlanRow({
              type: item.rowType,
              name: item.name,
              parent: null,
              mediaPlan: selectedPlanId,
            });

            if ('data' in res) {
              onSuccess(item.label || '');
              setRowState([res.data.id], { isNewAdded: true });
            }
            break;
          }
          case BUILDER_ROW_TYPES.PLATFORM:
          case BUILDER_ROW_TYPES.MEDIA_NETWORK: {
            startMeasurePerformance(TRACKING_EVENTS.PLATFORM_ADDITION);
            const channel = rows.find(
              ({ name }: { name: string }) => name === item.channelName,
            );

            if (!channel) {
              setRowToAdd(item);
              const res = await addMediaPlanRow({
                type: BUILDER_ROW_TYPES.CHANNEL,
                name: item.channelName,
                parent: null,
                mediaPlan: selectedPlanId,
              });

              if ('data' in res) {
                onSuccess(item.channelLabel || '');
                setRowState([res.data.id], { isNewAdded: true });
              } else {
                onFailed(item.channelLabel || '');
              }
              break;
            }
            const res = await addMediaPlanRow({
              type: item.rowType,
              name: item.platform,
              parent: channel.id,
              channelName: item.channelName,
              mediaPlan: selectedPlanId,
            });

            if ('data' in res) {
              const { data: newRow } = res;
              toggleRowExpanded?.(channel.id, true);
              toggleRowExpanded?.(newRow.id, true);
              onSuccess(item.label || '');
              setRowState([newRow.id], { isNewAdded: true });
            } else {
              onFailed(item.label || '');
            }
          }
        }
        return null;
      },
      // @ts-ignore
      canDrop: (
        item,
        monitor: DropTargetMonitor & {
          internalMonitor: { getTargetIds: () => string[] };
        },
      ) => monitor.internalMonitor?.getTargetIds().length < MAX_NUMBER_TARGETS,
      collect: (monitor: DropTargetMonitor) => ({
        isOver: monitor.isOver(),
        item: monitor.getItem(),
        canDrop: monitor.canDrop(),
      }),
    });

    useEffect(() => {
      if (!rowToAdd) {
        return;
      }
      const channel = rows.find(
        ({ name }: { name: string }) => name === rowToAdd.channelName,
      );

      // this api requests need refactoring
      if (channel) {
        const requestRow = async () => {
          const res = await addMediaPlanRow({
            type: rowToAdd.rowType,
            name: rowToAdd.platform,
            parent: channel.id,
            channelName: channel.name,
            mediaPlan: selectedPlanId,
          });

          if ('data' in res) {
            const { data: newRow } = res;
            toggleRowExpanded?.(channel.id, true);
            toggleRowExpanded?.(newRow.id, true);
            onSuccess(rowToAdd.label || '');
            setRowState([newRow.id], { isNewAdded: true });
          } else {
            onFailed(rowToAdd.label || '');
          }
        };

        requestRow();
        setRowToAdd(null);
      }
    }, [rows, rowToAdd]);

    return (
      <>
        <Container ref={drop} isHighlight={canDrop && isOver} />
        {isOver ? (
          <DropHelperOverlay $scale={scale}>
            <DropHelper isHighlight={canDrop && isOver}>
              <DropHelperContent>
                <Typography color={({ palette }) => palette.text.link}>
                  {t('Drag items here')}
                </Typography>
              </DropHelperContent>
            </DropHelper>
          </DropHelperOverlay>
        ) : null}
      </>
    );
  },
);

const Container = styled('div')<{ isHighlight: boolean }>`
  position: absolute;
  top: 0;
  width: 100%;
  height: inherit;
  z-index: -1;
`;

const DropHelperOverlay = styled('div')<{ $scale: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(
    100% - ${ADD_PANEL_WIDTH}px + ${DRAWER_TABS_WIDTH}px -
      ${CONTENT_PADDING_LEFT}px
  );
  height: 100%;
  background-color: ${({ theme }) => theme.palette.background.tertiary};
  border: 2px solid ${({ theme }) => theme.palette.action.focusBorder};
  border-radius: 8px;
  z-index: 99;
  display: flex;
`;

const DropHelper = styled('div')<{ isHighlight: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
`;

const DropHelperContent = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export default DropContainer;
